/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import axios, { AxiosResponse } from "axios";
import baseUrl from "..";
import { BatchCreationPayload, BatchCreationSuccessResponse, MasterActivitiesResponseList, MediaMasterResponseList, MixingRatioResponse } from "../../components/admin/mediaPreparationBatchInit/types/mediaBatchCreation.types";
import { authToken, authUserID, axiosInstance } from "../axiosConfig";
import { MediaBatchListResponse, MediaBatchLotListResponse, MediaSolutionBatchListResponse, MediaSolutionBatchLotListResponse, SolutionPowderMixingPayload } from "../../components/admin/mediaPreparationBatchInit/types/mediaBatchPreparation.types";
import { ComplianceCheckListResponse, MediaSterilization, MediaSterilizationSuccessResponse } from "../../components/admin/mediaPreparationBatchInit/types/mediaBatchSterilization.types";
import { MediaBatchViewPreparationDetails } from "../../components/admin/mediaPreparationBatchInit/types/mediaBatchCommon.types";
import { ContainerBatchResponse, ContainerDimensionResponse, ContainerLotResponse, ContainerResponse, ContainerTypeResponse, DispenseEntryPayload } from "../../components/admin/mediaPreparationBatchInit/types/mediaBatchDispense.types";
import { MediaFineTunningPayload } from "../../components/admin/mediaPreparationBatchInit/types/mediaFineTunning.types";
import { FinalQualityParaListResponse, ValidateUserPayload, SaveFinalQualityPayload } from "../../components/admin/mediaPreparationBatchInit/types/mediaFinalQuality.types";

const HEADERS = {
	"x-auth-token": authToken,
	"x-userid": authUserID,
};

const buildUrl = (
	apiEndpoint: string,
	params: Record<string, string | number | undefined>,
): string => {
	const filtered = Object.fromEntries(
		Object.entries(params).filter(([_, v]) => v !== undefined && v !== ""),
	);
	return `${baseUrl}/${apiEndpoint}?${new URLSearchParams(filtered as Record<string, string>)}`;
};

const getViewPreparationDetails = async (urlParams: any): Promise<MediaBatchViewPreparationDetails> => {
	const url = buildUrl("media/view_batch_details", urlParams);
	const response: AxiosResponse<MediaBatchViewPreparationDetails> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
};

// Media Batch Creation Related APIs.
const getMediaMasters = async (urlParams: any): Promise<MediaMasterResponseList> => {
	const url = buildUrl("media/list_media_masters", urlParams)
	const response: AxiosResponse<MediaMasterResponseList> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
};

const getMediaMasterActivities = async (urlParams: any): Promise<MasterActivitiesResponseList> => {
	const url = buildUrl("master_activities/list", urlParams)
	const response: AxiosResponse<MasterActivitiesResponseList> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
};

const postCreationBatch = async (payload: BatchCreationPayload): Promise<BatchCreationSuccessResponse> => {
	const url = buildUrl("media/create_media_preparation_batch", {});
	const response: AxiosResponse<BatchCreationSuccessResponse> = await axiosInstance.post(url, payload, { headers: HEADERS });
	return response.data;
}

const getMixingRatio = async (urlParams: any): Promise<MixingRatioResponse> => {
	const url = buildUrl("media/list_ratio_details", urlParams);
	const response: AxiosResponse<MixingRatioResponse> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
}


// Media Batch Preparation Related APIs.
const getMediaPowderBatches = async (urlParams: any): Promise<MediaBatchListResponse> => {
	const url = buildUrl("media_powder/list", urlParams);
	const response: AxiosResponse<MediaBatchListResponse> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
};

const getMediaPowderBatchLots = async (urlParams: any): Promise<MediaBatchLotListResponse> => {
	const url = buildUrl("media_powder/list_powder_batch_lot_details", urlParams);
	const response: AxiosResponse<MediaBatchLotListResponse> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
};

const getMediaSolutionBatches = async (urlParams: any): Promise<MediaSolutionBatchListResponse> => {
	const url = buildUrl("media/list_solution_batch", urlParams);
	const response: AxiosResponse<MediaSolutionBatchListResponse> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
}

const getMediaSolutionBatchLots = async (urlParams: any): Promise<MediaSolutionBatchLotListResponse> => {
	const url = buildUrl("media/list_solution_batch_lot_details", urlParams);
	const response: AxiosResponse<MediaSolutionBatchLotListResponse> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
}

const postPowderSolutionMixing = async (payload: SolutionPowderMixingPayload): Promise<any> => {
	const url = buildUrl("media_preparation/powder_solution_prep", {});
	const response: AxiosResponse<any> = await axiosInstance.post(url, payload, { headers: HEADERS });
	return response.data;
};

const getComplianceCheckList = async (): Promise<ComplianceCheckListResponse> => {
	const url = buildUrl("media_sterilization/list_checklist", {});
	const response: AxiosResponse<ComplianceCheckListResponse> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
};

const uploadFile = async (file: File) => {

  const formData = new FormData();
  formData.append("file", file);

    const response = await axiosInstance.post(
      `${baseUrl}/files/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
};

const postSterilization = async (payload: MediaSterilization): Promise<MediaSterilizationSuccessResponse> => {
	const url = buildUrl("media_sterilization/save", {});
	const response: AxiosResponse<MediaSterilizationSuccessResponse> = await axiosInstance.post(url, payload, { headers: HEADERS });
	return response.data;
};

const getContainers = async (urlParams: any): Promise<ContainerResponse> => {
	const url = buildUrl("container_inventory/list_container_batch_lot_details", urlParams);
	const response: AxiosResponse<ContainerResponse> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
};

const getContainerTypes = async (urlParams: any): Promise<ContainerTypeResponse>=> {
	const url = buildUrl("media/list_container_type_masters", urlParams);
	const response: AxiosResponse<ContainerTypeResponse> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
};

const getContainerDimensions = async (urlParams: any): Promise<ContainerDimensionResponse> => {
	const url = buildUrl("media/list_container_capacity_mappings", urlParams);
	const response: AxiosResponse<ContainerDimensionResponse> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
};

const getContainerBatches = async (urlParams: any): Promise<ContainerBatchResponse> => {
	const url = buildUrl("container_inventory/list", urlParams);
	const response: AxiosResponse<ContainerBatchResponse> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
};

const getContainerLots = async (urlParams: any): Promise<ContainerLotResponse> => {
	const url = buildUrl("container_inventory/list_container_batch_lot_details", urlParams);
	const response: AxiosResponse<ContainerLotResponse> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
};

const postDispenseEntry = async (payload: DispenseEntryPayload): Promise<DispenseEntryPayload> => {
	const url = buildUrl("media_dispensing/save", {});
	const response: AxiosResponse<DispenseEntryPayload> = await axiosInstance.post(url, payload, { headers: HEADERS });
	return response.data;
};

const postFineTunning = async (payload: MediaFineTunningPayload): Promise<any> => {
	const url = buildUrl("media_fine_tuning/save", {});
	const response: AxiosResponse<any> = await axiosInstance.post(url, payload, { headers: HEADERS });
	return response.data;
}

const getFinalQualityParameters = async (): Promise<FinalQualityParaListResponse> => {
	const url = buildUrl("media_final_quality_review/list_parameters", {});
	const response: AxiosResponse<FinalQualityParaListResponse> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
}

const validateSessionUser = async (payload: ValidateUserPayload): Promise<any> => {
	const url = buildUrl("media_batch_preparation/validate_session_user", {});
	const response: AxiosResponse<any> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
}

const saveFinalQualityReview = async (payload: SaveFinalQualityPayload): Promise<any> => {
	const url = buildUrl("media_final_quality_review/save", {});
	const response: AxiosResponse<any> = await axiosInstance.post(url, payload, { headers: HEADERS });
	return response.data;
};

const getCategoryList = async () => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/media/list_solution_categories?page=1&size=50&sort=id&order=desc&status=active`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};

export const MediaBatchPreparationInitServices = {
	getViewPreparationDetails,

	getMediaMasters,
	getMediaMasterActivities,
	postCreationBatch,
	getMixingRatio,

	getMediaPowderBatches,
	getMediaPowderBatchLots,

	getMediaSolutionBatches,
	getMediaSolutionBatchLots,
	postPowderSolutionMixing,

	uploadFile,

	getComplianceCheckList,
	postSterilization,
	getContainerTypes,
	getContainerBatches,
	getContainerLots,
	getContainers,
	getContainerDimensions,
	postDispenseEntry,

	postFineTunning,

	getFinalQualityParameters,
	validateSessionUser,
	saveFinalQualityReview,
    getCategoryList,

};
